import React, { useEffect, useMemo, useState } from "react";
import { Send, Loader2, MailCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import api, { formatCurrency, formatDate } from "../lib/api";
import { useTerms } from "../lib/BrandContext";
import { toast } from "sonner";

const TEMPLATES = {
  friendly: {
    label: "Friendly reminder",
    subject: "Friendly reminder — {charge}",
    body:
      "Hi, quick reminder that {charge} for {player_name} is still unpaid. " +
      "Amount due: {amount}. Please pay when you can and mark it paid in Squad Fee. " +
      "Thanks!\n\n— {leader}",
  },
  due_today: {
    label: "Due today",
    subject: "Due today — {charge}",
    body:
      "Heads up — {charge} for {player_name} ({amount}) is due today. " +
      "Pay through your usual method (Venmo, Zelle, Cash App, cash, or check) " +
      "and tap Mark as paid in Squad Fee once you do.\n\nThanks,\n{leader}",
  },
  overdue: {
    label: "Late payment",
    subject: "Overdue — {charge}",
    body:
      "Hi, {charge} for {player_name} is past due. It was due on {due_date} and the amount " +
      "is {amount}. Please send it over and mark it paid in Squad Fee so I can close out " +
      "our records.\n\nThank you,\n{leader}",
  },
  final: {
    label: "Final reminder",
    subject: "Final reminder — {charge}",
    body:
      "Last call on {charge} for {player_name} ({amount}). " +
      "Please pay this week or reach out if there's anything we need to work out. " +
      "After that I may need to follow up directly.\n\n— {leader}",
  },
  custom: {
    label: "Custom message",
    subject: "About {charge}",
    body: "",
  },
};

/**
 * Inline "Send reminder" dialog. Drops anywhere — Charges cards, Payments rows.
 *
 * Props
 *   open / onOpenChange   controlled
 *   charge                { id, title, amount, due_date }
 *   initialPaymentIds     pre-select specific recipients (e.g. one unpaid row)
 *   forceScope            "all_unpaid" | "selected" — locks the scope picker
 */
export default function SendReminderDialog({
  open,
  onOpenChange,
  charge,
  initialPaymentIds = [],
  forceScope,
}) {
  const { terms } = useTerms();
  const [template, setTemplate] = useState("friendly");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [unpaidRecords, setUnpaidRecords] = useState([]);
  const [selectedIds, setSelectedIds] = useState(initialPaymentIds);
  const [scope, setScope] = useState(forceScope || (initialPaymentIds.length ? "selected" : "all_unpaid"));
  const [sending, setSending] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  // Reset state every time we open with a different charge
  useEffect(() => {
    if (!open) return;
    setSelectedIds(initialPaymentIds);
    setScope(forceScope || (initialPaymentIds.length ? "selected" : "all_unpaid"));
    setTemplate("friendly");
    setShowEditor(false);
  }, [open, charge?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fill subject/body from the chosen template + current charge.
  //
  // `{player_name}` is left AS A LITERAL TEMPLATE TOKEN in the composed
  // body whenever more than one recipient could receive this message
  // (all-unpaid or multi-select). The backend expands the token per
  // recipient right before dispatch, so Joan sees Jake's name and Bob's
  // parent sees Bob's name — same subject/body payload, different
  // resolved values. When only one recipient is picked we substitute
  // the concrete name up front so the preview matches exactly what
  // gets sent.
  useEffect(() => {
    if (!charge) return;
    const t = TEMPLATES[template];
    const onePlayer =
      scope === "selected" && selectedIds.length === 1
        ? unpaidRecords.find((r) => r.id === selectedIds[0])?.player
        : null;
    const playerNameSubstitute = onePlayer
      ? `${onePlayer.first_name || ""} ${onePlayer.last_name || ""}`.trim()
      : "{player_name}"; // Backend fills per-recipient — never send a literal bracket placeholder to the recipient.
    const fill = (s) =>
      s
        .replaceAll("{charge}", charge.title || "[Charge Name]")
        .replaceAll("{amount}", formatCurrency(charge.amount || 0))
        .replaceAll("{due_date}", formatDate(charge.due_date))
        .replaceAll("{player_name}", playerNameSubstitute)
        .replaceAll("{leader}", terms.leader || "Coach");
    setSubject(fill(t.subject));
    setBody(fill(t.body));
  }, [charge, template, scope, selectedIds, unpaidRecords, terms]);

  // Load unpaid/rejected recipients for this charge
  useEffect(() => {
    if (!open || !charge?.id) return;
    setLoadingRecipients(true);
    api
      .get("/payments", { params: { charge_id: charge.id } })
      .then((r) => {
        const pool = (r.data || []).filter((x) => x.status === "unpaid" || x.status === "rejected");
        setUnpaidRecords(pool);
      })
      .catch(() => setUnpaidRecords([]))
      .finally(() => setLoadingRecipients(false));
  }, [open, charge?.id]);

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const selectAll = () => setSelectedIds(unpaidRecords.map((r) => r.id));
  const clearAll = () => setSelectedIds([]);

  const sendLabel = useMemo(() => {
    if (sending) return null;
    if (scope === "selected") return `Send to ${selectedIds.length} selected`;
    return `Send to all unpaid (${unpaidRecords.length})`;
  }, [scope, selectedIds.length, unpaidRecords.length, sending]);

  const send = async () => {
    if (!charge?.id) return;
    if (scope === "selected" && selectedIds.length === 0) {
      toast.error("Pick at least one recipient");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/reminders", {
        charge_id: charge.id,
        scope,
        payment_record_ids: scope === "selected" ? selectedIds : null,
        subject,
        body,
      });
      const sentCount = Number(data?.sent_count ?? 0);
      const failedCount = Number(data?.failed_count ?? 0);
      if (failedCount === 0 && sentCount > 0) {
        toast.success(
          sentCount === 1 ? "Reminder sent." : `Reminders sent to ${sentCount} people.`
        );
      } else if (sentCount > 0 && failedCount > 0) {
        toast.warning(
          `Sent to ${sentCount} of ${sentCount + failedCount} recipients.`,
          {
            description:
              failedCount === 1
                ? "1 email could not be delivered — try again in a few minutes."
                : `${failedCount} emails could not be delivered — try again in a few minutes.`,
          }
        );
      } else if (failedCount > 0 && sentCount === 0) {
        toast.error("Reminder could not be emailed.", {
          description: "Please try again in a few minutes.",
        });
      } else {
        // Zero eligible recipients (e.g. all players lacked linked parents).
        toast.warning("No recipients to email.", {
          description: "Link a parent to at least one player and try again.",
        });
      }
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="send-reminder-dialog">
        <DialogHeader>
          <DialogTitle>Send reminder</DialogTitle>
          <DialogDescription>
            For <span className="font-semibold text-ink-900">{charge?.title}</span>
            {charge?.amount ? <> · {formatCurrency(charge.amount)}</> : null}
            {charge?.due_date ? <> · due {formatDate(charge.due_date)}</> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Template</Label>
            <select
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              className="mt-1 h-11 w-full rounded-lg border border-ink-200 bg-white px-3"
              data-testid="reminder-template-select"
            >
              {Object.entries(TEMPLATES).map(([key, t]) => (
                <option key={key} value={key}>{t.label}</option>
              ))}
            </select>
            {!showEditor ? (
              <button
                type="button"
                onClick={() => setShowEditor(true)}
                className="mt-2 text-xs font-semibold text-brand hover:underline"
                data-testid="reminder-edit-message"
              >
                Edit message
              </button>
            ) : null}
          </div>

          {!forceScope ? (
            <div>
              <Label>Recipients</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setScope("all_unpaid")}
                  className={`rounded-lg border p-3 text-left text-sm font-semibold ${
                    scope === "all_unpaid"
                      ? "border-brand bg-brand-50 text-brand"
                      : "border-ink-200 bg-white text-ink-700 hover:bg-ink-100"
                  }`}
                  data-testid="reminder-scope-all"
                >
                  Send to all unpaid
                  <p className="mt-0.5 text-xs font-medium text-ink-500" data-testid="reminder-scope-all-count">
                    {unpaidRecords.length} {unpaidRecords.length === 1 ? "recipient" : "recipients"}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setScope("selected")}
                  className={`rounded-lg border p-3 text-left text-sm font-semibold ${
                    scope === "selected"
                      ? "border-brand bg-brand-50 text-brand"
                      : "border-ink-200 bg-white text-ink-700 hover:bg-ink-100"
                  }`}
                  data-testid="reminder-scope-selected"
                >
                  Selected people
                  <p className="mt-0.5 text-xs font-medium text-ink-500">Pick who gets this</p>
                </button>
              </div>
            </div>
          ) : null}

          {scope === "selected" ? (
            <div className="rounded-lg border border-ink-200 bg-white">
              <div className="flex items-center justify-between border-b border-ink-100 px-3 py-2">
                <p className="text-xs font-bold uppercase tracking-wide text-ink-500">
                  {selectedIds.length} / {unpaidRecords.length} selected
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-xs font-semibold text-brand hover:underline"
                    onClick={selectAll}
                    data-testid="reminder-recipients-select-all"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="text-xs font-semibold text-ink-500 hover:underline"
                    onClick={clearAll}
                    data-testid="reminder-recipients-clear"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-ink-100">
                {loadingRecipients ? (
                  <p className="p-3 text-sm text-ink-500">Loading…</p>
                ) : unpaidRecords.length === 0 ? (
                  <p className="p-3 text-sm text-ink-500">Everyone on this charge has already paid.</p>
                ) : (
                  unpaidRecords.map((r) => (
                    <label
                      key={r.id}
                      className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-ink-100/60"
                      data-testid={`reminder-recipient-${r.id}`}
                    >
                      <Checkbox
                        checked={selectedIds.includes(r.id)}
                        onCheckedChange={() => toggleSelect(r.id)}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink-900">
                          {r.player?.first_name} {r.player?.last_name}
                          {r.player?.jersey ? <span className="ml-1 text-ink-500">#{r.player.jersey}</span> : null}
                        </p>
                        <p className="truncate text-xs text-ink-500">
                          {r.parents?.length ? r.parents.map((u) => u.email).join(", ") : "No parent linked yet"}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs font-semibold uppercase text-rose-600">{r.status}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          ) : null}

          {showEditor ? (
            <>
              <div>
                <Label>Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="h-11"
                  data-testid="reminder-subject-input"
                />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea
                  rows={6}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  data-testid="reminder-body-input"
                />
                <p className="mt-1 text-xs text-ink-500" data-testid="reminder-body-tokens-hint">
                  Tip: <code className="rounded bg-ink-100 px-1 font-mono text-[11px]">{"{player_name}"}</code>
                  {" "}is replaced with each recipient's {terms.athlete} name.
                </p>
              </div>
            </>
          ) : null}
        </div>

        <DialogFooter>
          <div className="flex w-full items-center justify-between gap-3 text-xs text-ink-500">
            <p>
              <MailCheck className="mr-1 inline h-3.5 w-3.5 text-brand" />
              Reminder will be sent by email.
            </p>
            <Button
              onClick={send}
              disabled={sending || !charge?.id}
              className="h-11 bg-brand font-semibold text-white hover:bg-brand-hover"
              data-testid="reminder-send-button"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1 h-4 w-4" /> {sendLabel}</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
